// frontend/src/components/themed/ThemedSearchableSelect.js - Professional themed searchable select component using react-select
import React from 'react';
import Select from 'react-select';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * ThemedSearchableSelect Component
 *
 * A searchable dropdown component that allows users to type directly to filter options.
 * Built on react-select with custom theming to match light/dark mode.
 *
 * Features:
 * - Type-to-search filtering
 * - Keyboard navigation support
 * - Loading states
 * - Custom option rendering
 * - Theme-aware styling
 *
 * @param {Object} props
 * @param {string} props.label - Label text displayed above the select
 * @param {Array} props.options - Array of option objects with {value, label} structure
 * @param {Object} props.value - Currently selected option object
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.placeholder - Placeholder text when no selection
 * @param {boolean} props.isLoading - Show loading indicator
 * @param {string} props.helperText - Helper text displayed below the select
 * @param {boolean} props.isDisabled - Disable the select
 * @param {Function} props.formatOptionLabel - Custom option renderer function
 * @param {Function} props.noOptionsMessage - Custom message when no options found
 */
const ThemedSearchableSelect = ({
  label,
  options = [],
  value = null,
  onChange,
  placeholder = 'Search...',
  isLoading = false,
  helperText,
  isDisabled = false,
  formatOptionLabel,
  noOptionsMessage,
  ...props
}) => {
  const { text, cn, theme } = useTheme();

  const isDarkMode = theme === 'dark';

  /**
   * Custom styles for react-select to match the application theme
   * Provides proper contrast for both light and dark modes
   */
  const customStyles = {
    // Main container
    control: (base, state) => ({
      ...base,
      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff', // gray-800 : white
      borderColor: state.isFocused
        ? '#3b82f6' // blue-500
        : isDarkMode ? '#4b5563' : '#d1d5db', // gray-600 : gray-300
      borderWidth: '1px',
      borderRadius: '0.375rem',
      minHeight: '42px',
      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
      '&:hover': {
        borderColor: state.isFocused
          ? '#3b82f6'
          : isDarkMode ? '#6b7280' : '#9ca3af' // gray-500 : gray-400
      },
      cursor: 'pointer'
    }),

    // Dropdown menu
    menu: (base) => ({
      ...base,
      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff', // gray-800 : white
      border: isDarkMode ? '1px solid #4b5563' : '1px solid #e5e7eb', // gray-600 : gray-200
      boxShadow: isDarkMode
        ? '0 10px 15px -3px rgb(0 0 0 / 0.5), 0 4px 6px -4px rgb(0 0 0 / 0.5)'
        : '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
      borderRadius: '0.375rem',
      zIndex: 9999
    }),

    // Menu list container
    menuList: (base) => ({
      ...base,
      padding: '4px',
      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff' // gray-800 : white
    }),

    // Individual option
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#3b82f6' // blue-500 for selected
        : state.isFocused
        ? isDarkMode ? '#374151' : '#dbeafe' // gray-700 : blue-100
        : 'transparent',
      color: state.isSelected
        ? '#ffffff' // white text for selected
        : isDarkMode ? '#f9fafb' : '#111827', // gray-50 : gray-900
      cursor: 'pointer',
      padding: '8px 12px',
      borderRadius: '0.25rem',
      '&:active': {
        backgroundColor: '#3b82f6' // blue-500
      }
    }),

    // Selected value display
    singleValue: (base) => ({
      ...base,
      color: isDarkMode ? '#f9fafb' : '#111827' // gray-50 : gray-900
    }),

    // Multi-value chips (for multi-select)
    multiValue: (base) => ({
      ...base,
      backgroundColor: isDarkMode ? '#374151' : '#dbeafe', // gray-700 : blue-100
      borderRadius: '0.25rem'
    }),

    multiValueLabel: (base) => ({
      ...base,
      color: isDarkMode ? '#f9fafb' : '#111827' // gray-50 : gray-900
    }),

    multiValueRemove: (base) => ({
      ...base,
      color: isDarkMode ? '#9ca3af' : '#6b7280', // gray-400 : gray-500
      '&:hover': {
        backgroundColor: '#ef4444', // red-500
        color: '#ffffff'
      }
    }),

    // Search input
    input: (base) => ({
      ...base,
      color: isDarkMode ? '#f9fafb' : '#111827' // gray-50 : gray-900
    }),

    // Placeholder text
    placeholder: (base) => ({
      ...base,
      color: isDarkMode ? '#9ca3af' : '#6b7280' // gray-400 : gray-500
    }),

    // Dropdown indicator arrow
    dropdownIndicator: (base) => ({
      ...base,
      color: isDarkMode ? '#9ca3af' : '#6b7280', // gray-400 : gray-500
      '&:hover': {
        color: isDarkMode ? '#d1d5db' : '#4b5563' // gray-300 : gray-600
      }
    }),

    // Clear indicator (X button)
    clearIndicator: (base) => ({
      ...base,
      color: isDarkMode ? '#9ca3af' : '#6b7280', // gray-400 : gray-500
      '&:hover': {
        color: '#ef4444' // red-500
      }
    }),

    // Loading indicator
    loadingIndicator: (base) => ({
      ...base,
      color: isDarkMode ? '#9ca3af' : '#6b7280' // gray-400 : gray-500
    }),

    // "No options" message
    noOptionsMessage: (base) => ({
      ...base,
      color: isDarkMode ? '#9ca3af' : '#6b7280', // gray-400 : gray-500
      padding: '8px 12px'
    })
  };

  return (
    <div className="w-full">
      {/* Label */}
      {label && (
        <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
          {label}
        </label>
      )}

      {/* React Select Component */}
      <Select
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isLoading={isLoading}
        isDisabled={isDisabled}
        styles={customStyles}
        isSearchable={true}
        formatOptionLabel={formatOptionLabel}
        noOptionsMessage={noOptionsMessage}
        classNamePrefix="react-select"
        {...props}
      />

      {/* Helper Text */}
      {helperText && (
        <p className={cn("mt-1 text-sm", text.muted)}>
          {helperText}
        </p>
      )}
    </div>
  );
};

export default ThemedSearchableSelect;
